import { useState, useEffect } from 'react'
import { Copy, Check, Eye, EyeOff, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { copyToClipboard } from '@/utils/clipboard'
import RevealWarningDialog from '@/components/dialogs/RevealWarningDialog'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog'

interface RecoveryPhraseProps {
  mnemonic?: string
  onGenerate?: () => string
  showCopyButton?: boolean
  isRevealed?: boolean
  className?: string
  /** Pide confirmar 3 pasos de advertencia antes de revelar (ej. al ver una wallet ya existente). */
  requireWarningGate?: boolean
}

const RecoveryPhrase = ({
  mnemonic: providedMnemonic,
  onGenerate,
  showCopyButton = true,
  isRevealed: initialRevealed = false,
  className = '',
  requireWarningGate = false
}: RecoveryPhraseProps) => {
  const { t } = useTranslation()
  const [mnemonic, setMnemonic] = useState<string>(providedMnemonic || '')
  const [words, setWords] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [isRevealed, setIsRevealed] = useState(initialRevealed)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  const [showCopyConfirm, setShowCopyConfirm] = useState(false)

  // Generate or set mnemonic on mount
  useEffect(() => {
    if (providedMnemonic) {
      setMnemonic(providedMnemonic)
      setWords(providedMnemonic.trim().split(/\s+/))
    } else if (onGenerate) {
      const generated = onGenerate()
      setMnemonic(generated)
      setWords(generated.trim().split(/\s+/))
    }
  }, [providedMnemonic, onGenerate])

  const confirmCopy = async () => {
    const success = await copyToClipboard(mnemonic)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const toggleReveal = () => {
    if (isRevealed) {
      setIsRevealed(false)
      return
    }
    if (requireWarningGate) {
      setShowWarningDialog(true)
      return
    }
    setIsRevealed(true)
  }

  /*   if (!mnemonic || words.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-text-secondary">
        <p>No recovery phrase available</p>
      </div>
    )
  }
 */
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-text-secondary font-medium">
          {t('recover.word_phrase', { count: words.length })}
        </p>
        <div className="flex gap-2">
          <button
            onClick={toggleReveal}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card hover:bg-card/80 transition-colors text-text-secondary text-sm"
          >
            {isRevealed ? (
              <>
                <EyeOff className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.hide')}</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.reveal')}</span>
              </>
            )}
          </button>
          {showCopyButton && (
            <button
              onClick={() => setShowCopyConfirm(true)}
              disabled={!isRevealed}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card hover:bg-card/80 transition-colors text-text-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-accent" />
                  <span className="hidden sm:inline text-accent">{t('common.copied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('common.copy')}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Words grid with blur overlay */}
      <div className="relative">
        {/* Blur overlay when not revealed */}
        {!isRevealed && (
          <div className="absolute inset-0 backdrop-blur-md bg-card/30 rounded-xl z-10 flex items-center justify-center">
            <button
              onClick={toggleReveal}
              className="flex flex-col items-center gap-2 text-text-main hover:text-primary transition-colors"
            >
              <Eye className="w-8 h-8" />
              <p className="text-sm font-medium">{t('common.click_reveal')}</p>
            </button>
          </div>
        )}

        {/* Words grid */}
        <div className="grid grid-cols-2 gap-2 p-4 bg-card rounded-xl border border-red-500/30">
          {words.map((word, index) => (
            <div
              key={index}
              className="relative flex items-center gap-2 bg-card-e2/80 rounded-lg p-3"
            >
              <span className="text-xs text-text-secondary">{index + 1}</span>
              {/* Word text */}
              <span className="text-text-main font-bold pl-2">{word}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Warning message */}
      <div className="flex gap-3 p-4 bg-card rounded-xl border border-red-500/30">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
            <TriangleAlert className="h-4 w-4 text-red-500" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-main">
            {t('recover.warning_title')}
          </p>
          <p className="text-sm text-text-secondary leading-relaxed">
            {t('recover.phrase_warning_desc')}
          </p>
        </div>
      </div>

      {requireWarningGate && (
        <RevealWarningDialog
          open={showWarningDialog}
          onClose={() => setShowWarningDialog(false)}
          onConfirm={() => setIsRevealed(true)}
          variant="phrase"
        />
      )}

      <ConfirmDialog
        open={showCopyConfirm}
        onClose={() => setShowCopyConfirm(false)}
        onConfirm={confirmCopy}
        variant="danger"
        title={t('recover.copy_confirm_title')}
        description={t('recover.copy_phrase_confirm_desc')}
        confirmLabel={t('recover.copy_confirm_button')}
      />
    </div>
  )
}

export default RecoveryPhrase
